import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { FetchResult, useMutation } from '@apollo/client';
import { TeamsModalState, UserSidesheetState } from '../ts/interfaces/teams';
import { UPDATE_USER } from '../apollo/mutations/user';
import {
    showErrorNotification,
    showSuccessNotification,
} from '../notifications';
import { HierarchyUser, UpdateUserInput } from '../codegen/graphql';
import { GET_OPS_HIERARCHY } from '../apollo/queries/opsHierarchy';
import { GET_USERS } from '../apollo/queries/user';
import { GET_STORES } from '../apollo/queries/store';

interface UserPortalContextType {
    userSidesheet: UserSidesheetState;
    setUserSidesheet: React.Dispatch<React.SetStateAction<UserSidesheetState>>;
    teamsModal: TeamsModalState;
    setTeamsModal: React.Dispatch<React.SetStateAction<TeamsModalState>>;
    closeUserSidesheet: () => void;
    closeTeamsModal: () => void;
    openUserSidesheet: (user?: HierarchyUser | null) => void;
    openTeamsModal: (modal: Partial<TeamsModalState>) => void;
    user: HierarchyUser | null;
    updateUser: (input: UpdateUserInput) => Promise<FetchResult>;
    updateUserLoading: boolean;
}

const initialUserSidesheetState: UserSidesheetState = {
    opened: false,
};

const initialTeamsModalState: TeamsModalState = {
    opened: false,
    movingStoreManager: false,
    addingStoreManager: false,
    storeManager: null,
};

const UserPortalContext = createContext<UserPortalContextType>({
    userSidesheet: initialUserSidesheetState,
    setUserSidesheet: () => {},
    teamsModal: initialTeamsModalState,
    setTeamsModal: () => {},
    closeUserSidesheet: () => {},
    closeTeamsModal: () => {},
    openUserSidesheet: () => {},
    openTeamsModal: () => {},
    user: null,
    updateUser: () => Promise.resolve({} as FetchResult),
    updateUserLoading: false,
});

type Props = Readonly<React.PropsWithChildren>;

export function UserPortalProvider({ children }: Props) {
    const [userSidesheet, setUserSidesheet] = useState<UserSidesheetState>(
        initialUserSidesheetState,
    );
    const [user, setUser] = useState<HierarchyUser | null>(null);
    const [teamsModal, setTeamsModal] = useState<TeamsModalState>(
        initialTeamsModalState,
    );

    const [
        updateUserMutation,
        {
            data: updateUserData,
            error: updateUserError,
            loading: updateUserLoading,
        },
    ] = useMutation(UPDATE_USER, {
        refetchQueries: [GET_USERS, GET_OPS_HIERARCHY, GET_STORES],
        awaitRefetchQueries: true,
    });

    const updateUser = useCallback(
        async (variables: UpdateUserInput) =>
            updateUserMutation({
                variables: { input: variables },
            }),
        [updateUserMutation],
    );

    const openUserSidesheet = useCallback(
        (inputUser?: HierarchyUser | null) => {
            if (inputUser) {
                setUser(inputUser);
            }
            setUserSidesheet({
                opened: true,
            });
        },
        [setUserSidesheet],
    );

    const closeUserSidesheet = useCallback(() => {
        setUserSidesheet({
            opened: false,
        });
        setUser(null);
    }, [setUserSidesheet]);

    const openTeamsModal = useCallback(
        (modal: Omit<Partial<TeamsModalState>, 'opened'>) => {
            setTeamsModal({
                ...initialTeamsModalState,
                ...modal,
                opened: true,
            });
        },
        [setTeamsModal],
    );

    const closeTeamsModal = useCallback(() => {
        setTeamsModal(initialTeamsModalState);
    }, [setTeamsModal]);

    const value = useMemo(
        () => ({
            userSidesheet,
            setUserSidesheet,
            teamsModal,
            setTeamsModal,
            closeUserSidesheet,
            closeTeamsModal,
            openUserSidesheet,
            openTeamsModal,
            user,
            updateUser,
            updateUserLoading,
        }),
        [
            userSidesheet,
            setUserSidesheet,
            teamsModal,
            setTeamsModal,
            closeUserSidesheet,
            closeTeamsModal,
            openUserSidesheet,
            openTeamsModal,
            user,
            updateUser,
            updateUserLoading,
        ],
    );

    useEffect(() => {
        if (updateUserError) {
            showErrorNotification('Error updating store manager');
        } else if (updateUserData) {
            showSuccessNotification('Store manager updated successfully');
        }
    }, [updateUserError, updateUserData]);

    return (
        <UserPortalContext.Provider value={value}>
            {children}
        </UserPortalContext.Provider>
    );
}

export const useUserPortal = () => {
    const context = useContext(UserPortalContext);

    if (!context) {
        throw new Error(
            'useUserPortal must be used within a UserPortalProvider',
        );
    }

    return context;
};
